import type React from 'react';
import { useCallback, useMemo, useState } from 'react';

import type { PaginationProps } from '~/components/core/Tables/SortableTable/TablePaginationSection/types';
import { COLUMN_KEYS, EXTENDABLE_COLUMN_KEYS } from '~/components/Finances/FinancesTable/constants';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import useDataFetcher from '~/components/useDataFetcher';
import type { Exposure, FinancesWrapper } from '~/server_shared/swagger/data-contracts';
import { formatSortOrder } from '~/Utils';

export const DISPLAY_COLUMN_IDS = [
  COLUMN_KEYS.request_date,
  COLUMN_KEYS.payees,
  COLUMN_KEYS.service_start_date,
  COLUMN_KEYS.service_end_date,
  COLUMN_KEYS.payment_period,
  COLUMN_KEYS.amount,
  COLUMN_KEYS.financial_status,
  COLUMN_KEYS.actions,

  EXTENDABLE_COLUMN_KEYS.issued_by,
  EXTENDABLE_COLUMN_KEYS.payment_type,
  EXTENDABLE_COLUMN_KEYS.sent_to,
  EXTENDABLE_COLUMN_KEYS.reference,
  EXTENDABLE_COLUMN_KEYS.note,
  EXTENDABLE_COLUMN_KEYS.deductible_applied,
  EXTENDABLE_COLUMN_KEYS.payment_method,
  EXTENDABLE_COLUMN_KEYS.vat_details,
] as const;

type FilterParams = {
  payable_types_filter: ['indemnity'];
  exposures_filters: number[];
  page: number;
  results_per_page: number;
  sort_by_column: { id: (typeof DISPLAY_COLUMN_IDS)[number]; order: 'desc' | 'asc' };
};

const useIncomeProtectionScreenPaginatedPayments = ({
  claim,
  exposure,
}: {
  claim: ClaimPolymorph;
  exposure: Exposure & { id: number };
}) => {
  const [filterParams, setFilterParams] = useState<FilterParams>({
    page: 0,
    results_per_page: 10,
    sort_by_column: { id: COLUMN_KEYS.service_end_date, order: 'desc' },
    payable_types_filter: ['indemnity'],
    exposures_filters: [exposure.id],
  });

  const { data, isLoading, isError, reloadData } = useDataFetcher<FinancesWrapper>(
    `/api/v1/claims/${claim.id}/finances`,
    {
      params: {
        ...filterParams,
        page: filterParams.page + 1,
        sort_by_column: formatSortOrder(filterParams.sort_by_column),
      },
    }
  );

  const onChangePage = useCallback((_: unknown, page: number) => {
    setFilterParams((prevParams) => ({
      ...prevParams,
      page,
    }));
  }, []);

  const onChangeRowsPerPage: PaginationProps['onChangeRowsPerPage'] = useCallback(
    (event: React.ChangeEvent<{ value: string }>) => {
      // value is a number from our rowsPerPageOptions array, but we're rolling with the deceptive types
      setFilterParams((prevParams) => ({
        ...prevParams,
        results_per_page: parseInt(event.target.value),
        page: 0,
      }));
    },
    []
  );

  const onSortByColumn = useCallback(
    (_: unknown, sort_by_column: { id: (typeof DISPLAY_COLUMN_IDS)[number]; order: 'desc' | 'asc' }) => {
      setFilterParams((prevParams) => ({
        ...prevParams,
        sort_by_column,
        page: 0,
      }));
    },
    []
  );

  const paginationProps: PaginationProps = useMemo(() => {
    return {
      page: filterParams.page,
      rowsPerPage: filterParams.results_per_page,
      count: data?.count ?? 0,
      onChangePage,
      onChangeRowsPerPage,
      rowsPerPageOptions: [5, 10, 20],
    };
  }, [filterParams.page, filterParams.results_per_page, data?.count, onChangePage, onChangeRowsPerPage]);

  const isFetching = isLoading && Boolean(data);

  return {
    data,
    isLoading: isLoading && !isFetching,
    isFetching,
    isError,
    reloadData,
    filterParams,
    paginationProps,
    onSortByColumn,
  };
};

export default useIncomeProtectionScreenPaginatedPayments;
